/**
 * In-session mood check-in capture (VER-118).
 *
 * The server owns the rules: sessions 2–5 only, score 1–5, one reading per participant per
 * session (a re-submission REPLACES the previous one). The client mirrors the window purely to
 * decide whether to render the panel — it is never the enforcement point.
 */
import { errorFromResponse, getData, postData } from "./rest";

export type MoodCheckInPayload = {
    participantId: number;
    programmeId: number;
    sessionId: number;
    /** 1 (very low) to 5 (very good). */
    score: number;
};

export type MoodCheckInRecorded = {
    id: number;
    participantId: number;
    sessionId: number;
    score: number;
    capturedAt: string;
    sessionNumber: number;
};

/** One stored reading as listed for a session. Carries no participant name — the caller has those. */
export type SessionMoodCheckIn = {
    id: number;
    participantId: number;
    score: number;
    capturedAt: string;
};

/**
 * The readings already stored for a session. Without this the capture panel opens blank on a
 * revisit, so a facilitator cannot tell a recorded session from an unrecorded one.
 */
export const listSessionMoodCheckIns = async (sessionId: number): Promise<SessionMoodCheckIn[]> => {
    const { items } = await getData<{ items: SessionMoodCheckIn[]; sessionNumber: number }>(
        `/mood-check-ins?sessionId=${sessionId}`,
    );
    return items;
};

export const recordMoodCheckIn = async (payload: MoodCheckInPayload): Promise<MoodCheckInRecorded> => {
    const response = await postData("/mood-check-ins", payload);
    if (!response.ok) {
        // Carry the server's reason through — the S2-S5 window rule, the range rule and the
        // consent refusal are all things the facilitator can act on, and a bare status is not.
        throw await errorFromResponse(response, "save mood check-in");
    }
    return (await response.json()) as MoodCheckInRecorded;
};
